import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TIPOS_GASTO_606 } from "@/lib/retentions";
import { PurchaseKindPicker, YesNo } from "./PurchaseKindPicker";
import { SupplierField } from "./SupplierField";
import { VoucherFields } from "./VoucherFields";
import { InventoryLines, ConceptLines } from "./PurchaseLines";
import { PurchaseRetentions } from "./PurchaseRetentions";
import { EMPTY_CONCEPT, PAYMENT_METHODS, lineTotals, localToday } from "./purchaseConfig";

const sel = "mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm";
const blankSupplier = () => ({ mode: "registered", supplier: null, occ: { name: "", rnc: "", phone: "", id_type: "", save: true } });

export function PurchaseFormDialog({ open, onClose, onSaved }) {
  const [kind, setKind] = useState(null);
  const [voucher, setVoucher] = useState("E31");
  const [ncf, setNcf] = useState("");
  const [sup, setSup] = useState(blankSupplier());
  const [f, setF] = useState({ date: localToday(), invoice_ref: "", payment_method: "credito", tipo_gasto_606: "02" });
  const [inventory, setInventory] = useState(null);
  const [updateCosts, setUpdateCosts] = useState(true);
  const [lines, setLines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sug, setSug] = useState(null);
  const [ret, setRet] = useState({ itbis_retenido: 0, isr_retenido: 0, isr_retention_type: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setKind(null); setVoucher("E31"); setNcf(""); setSup(blankSupplier()); setInventory(null); setLines([]); setSug(null);
    setF({ date: localToday(), invoice_ref: "", payment_method: "credito", tipo_gasto_606: "02" });
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
    api.get("/expense-categories").then(({ data }) => setCategories(data));
  }, [open]);

  const voucherType = kind === "informal" ? "E41" : voucher;
  const supplierRnc = sup.mode === "occasional" ? sup.occ.rnc : sup.supplier?.rnc_cedula || "";
  const totals = useMemo(() => lineTotals(lines), [lines]);
  const payloadLines = lines.map((l) => ({ ...l, quantity: Number(l.quantity || 0), price: Number(l.price || 0) }));

  useEffect(() => {
    clearTimeout(timer.current);
    if (!kind || !lines.length) { setSug(null); setRet({ itbis_retenido: 0, isr_retenido: 0, isr_retention_type: "" }); setPending(false); return; }
    setPending(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/purchases/retentions/suggest", { voucher_type: voucherType, supplier_rnc: supplierRnc, date: f.date, items: payloadLines });
        setSug(data);
        setRet({ itbis_retenido: data.itbis_retenido, isr_retenido: data.isr_retenido, isr_retention_type: data.isr_retention_type });
      } catch (_e) { setSug(null); }
      finally { setPending(false); }
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, voucherType, supplierRnc, f.date, JSON.stringify(payloadLines)]);

  const chooseInventory = (v) => { setInventory(v); setLines(v ? [] : [{ ...EMPTY_CONCEPT, kind: kind === "informal" ? "servicio" : "bien" }]); };

  const submit = async () => {
    if (!lines.length) return toast.error("Agrega al menos una línea");
    setBusy(true);
    try {
      const occ = sup.mode === "occasional";
      const { data } = await api.post("/purchases", {
        purchase_kind: kind, voucher_type: voucherType, date: f.date,
        supplier_id: occ ? null : sup.supplier?.id || null,
        supplier_name: occ ? sup.occ.name : sup.supplier?.name || "",
        supplier_rnc: supplierRnc, supplier_phone: occ ? sup.occ.phone : "",
        supplier_id_type: occ ? (kind === "informal" ? "cedula" : sup.occ.id_type || "rnc") : "", save_supplier: occ && sup.occ.save,
        supplier_ncf: kind === "formal" ? ncf : "", invoice_ref: f.invoice_ref, payment_method: f.payment_method,
        affects_inventory: inventory, update_stock: inventory, update_costs: inventory && updateCosts,
        tipo_gasto_606: inventory ? "09" : f.tipo_gasto_606, items: payloadLines,
        itbis_retenido: Number(ret.itbis_retenido || 0), isr_retenido: Number(ret.isr_retenido || 0), isr_retention_type: ret.isr_retention_type,
      });
      toast.success(`Compra ${data.number} registrada${data.is_electronic ? " · enviando E41 a DGII" : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="purchase-form-dialog">
        <DialogHeader><DialogTitle>Nueva compra</DialogTitle></DialogHeader>
        <Step n={1} title="¿La compra es formal o informal?"><PurchaseKindPicker value={kind} onChange={(v) => { setKind(v); setSup(blankSupplier()); setNcf(""); }} /></Step>
        {kind && (
          <>
            <Step n={2} title="Proveedor y comprobante">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label className="overline">Proveedor</Label><SupplierField kind={kind} suppliers={suppliers} value={sup} onChange={setSup} /></div>
                <VoucherFields kind={kind} voucher={voucher} setVoucher={setVoucher} ncf={ncf} setNcf={setNcf} />
                <div><Label className="overline">Fecha</Label><Input type="date" className="mt-1" value={f.date} onChange={set("date")} data-testid="purchase-date" /></div>
                <div><Label className="overline">Pago</Label>
                  <select className={sel} value={f.payment_method} onChange={set("payment_method")} data-testid="purchase-payment">
                    {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select></div>
                <div><Label className="overline">Factura del proveedor (opcional)</Label><Input className="mt-1 font-mono" value={f.invoice_ref} onChange={set("invoice_ref")} data-testid="purchase-invoice-ref" /></div>
              </div>
            </Step>
            <Step n={3} title="¿Esta compra entra al inventario?">
              <div className="flex flex-wrap items-center gap-4">
                <YesNo value={inventory} onChange={chooseInventory} testid="purchase-inventory" />
                {inventory === true && (
                  <label className="flex items-center gap-2 text-sm"><Switch checked={updateCosts} onCheckedChange={setUpdateCosts} data-testid="purchase-update-costs" />Actualizar el costo en la ficha del producto</label>
                )}
                {inventory === false && (
                  <div className="flex items-center gap-2"><Label className="overline">Tipo de gasto (606)</Label>
                    <select className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" value={f.tipo_gasto_606} onChange={set("tipo_gasto_606")} data-testid="purchase-606-type">
                      {TIPOS_GASTO_606.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </select></div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {inventory === true ? "Suma existencia. El costo de cada línea es lo que pagaste; el interruptor decide si reemplaza el costo de la ficha."
                  : inventory === false ? "No toca existencia: cada línea va a su cuenta de gasto. Marca Servicio o Alquiler cuando aplique retención de ISR."
                  : "Elige Sí o No para continuar."}
              </p>
            </Step>
            {inventory !== null && (
              <Step n={4} title="Líneas">
                {inventory ? <InventoryLines lines={lines} setLines={setLines} updateCosts={updateCosts} />
                  : <ConceptLines lines={lines} setLines={setLines} categories={categories} />}
                {lines.length > 0 && <div className="mt-3"><PurchaseRetentions totals={totals} sug={sug} ret={ret} setRet={setRet} /></div>}
              </Step>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="purchase-cancel">Cancelar</Button>
          <Button className="bg-primary font-bold" onClick={submit} disabled={busy || pending || !kind || inventory === null || !lines.length} data-testid="purchase-submit">
            {kind === "informal" ? "Registrar y emitir E41" : "Registrar compra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, title, children }) {
  return (
    <section className="space-y-2" data-testid={`purchase-step-${n}`}>
      <div className="flex items-center gap-2 text-sm font-bold">
        <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] grid place-items-center">{n}</span>{title}
      </div>
      {children}
    </section>
  );
}
